
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { Getter } from "vuex-class";
import api from "@/services/api.service";
import { NearbyArea, NearbyAreasPlatform } from "@/types/sv-data/groups/NearbyArea";
import { renderDistance } from "@/filters";

const MetresToFeet = 3.280839895;

@Component
export default class NearbyAreas extends Vue {
	/** Raise location; null when no location is available yet (no stale results shown). */
	@Prop({ default: null }) public location: { lat: number; lng: number } | null;

	/** Maximum areas to request (server caps at 5). */
	@Prop({ default: 5 }) public maxResults: number;

	/** Which manual-raise platform this list is shown on — drives the server-side feature gate. */
	@Prop({ required: true }) public platform: NearbyAreasPlatform;

	/** Message shown while there is no location to rank against. */
	@Prop({ default: "Waiting for a location…" }) public awaitingText: string;

	/** Optional pill next to the header explaining what the distances are measured from. */
	@Prop({ default: "" }) public badgeText: string;

	/** Desktop (in-dropdown) hosts set this to separate the list from the search/tree below. */
	@Prop({ default: false }) public showSearchDivider: boolean;

	@Getter("getUseImperialMeasurements") isImperial: boolean;

	private areas: NearbyArea[] = [];
	private loading = false;

	public get hasLocation(): boolean {
		return (
			!!this.location &&
			typeof this.location.lat === "number" &&
			typeof this.location.lng === "number"
		);
	}

	@Watch("location", { immediate: true, deep: true })
	private async onLocationChanged(): Promise<void> {
		await this.fetch();
	}

	private async fetch(): Promise<void> {
		if (!this.hasLocation) {
			this.areas = [];
			return;
		}

		// Guard against out-of-order responses: the location can change mid-flight, so capture it
		// and discard this result if it is no longer current (each host hands us a fresh object).
		const requestedLocation = this.location;
		this.loading = true;
		try {
			const areas = await api.getNearbyAreas(this.location.lat, this.location.lng, this.platform, this.maxResults);
			if (this.location !== requestedLocation) {
				return;
			}
			this.areas = areas;
		} catch {
			if (this.location === requestedLocation) {
				this.areas = [];
			}
		} finally {
			if (this.location === requestedLocation) {
				this.loading = false;
			}
		}
	}

	public formatDistance(meters: number): string {
		const value = this.isImperial ? meters * MetresToFeet : meters;
		return renderDistance(value, this.isImperial);
	}

	public select(area: NearbyArea): void {
		this.$emit("select", area);
	}
}
